import { Component, NgZone, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EmailEditorComponent } from 'angular-email-editor';
import { ConfirmationService } from 'primeng/api';
import { Observable } from 'rxjs';
import { IPendingChangesComponent } from 'src/app/guards/pending-changes.guard';
import { EditorType, EXPERT_MODE_HELP_HTML, unlayerDesignHasContent } from 'src/app/models/communication/email-html-import';
import { Template } from 'src/app/models/communication/template';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';
import { ExpertHtmlEditorComponent } from '../expert-html-editor/expert-html-editor.component';

@Component({
  selector: 'app-template-details',
  templateUrl: './template-details.component.html',
  styleUrls: ['./template-details.component.scss']
})
export class TemplateDetailsComponent implements IPendingChangesComponent {
	@ViewChild(EmailEditorComponent) emailEditor: EmailEditorComponent;
	@ViewChild(ExpertHtmlEditorComponent) expertEditor: ExpertHtmlEditorComponent;

	public template: Template;
	public templates: Array<Template> = [];
	public selectedTemplate: Template;

	public editorOptions = {};

	// Designer (Unlayer) vs Expert (HTML). modeSelection is what the toggle shows; it is
	// snapped back to template.editorTypeID when the user cancels a switch.
	public editorType = EditorType;
	public expertHelp = EXPERT_MODE_HELP_HTML;
	public modeSelection: number = EditorType.Designer;
	// The Designer content as it was when the user switched to Expert, kept in memory so
	// switching back restores it. (Designer → Expert converts the design to HTML; HTML edits
	// can't be turned back into a design.)
	private designBeforeHtml: string = null;
	public modeOptions = [
		{ label: 'Designer', value: EditorType.Designer, icon: 'pi pi-th-large' },
		{ label: 'Expert (HTML)', value: EditorType.Html, icon: 'pi pi-code' }
	];

	private isNew: boolean = false;
	public pageLoaded: boolean = false;
	public errors: any = {};
	public isDirty: boolean = false;

	constructor(private dataService: DataService, private siteService:SiteService, private route: ActivatedRoute, private router: Router, private confirmationService: ConfirmationService, private zone: NgZone) { }

	ngOnInit() {
		this.route.paramMap.subscribe(p => {
			let id = p.get('id');
			if(id == 'new') {
				this.siteService.pageTitle = 'New Template';
				this.template = new Template({ templateID: crypto.randomUUID() });
				this.modeSelection = this.template.editorTypeID;
				this.isNew = true;
			} else {
				this.refreshTemplate(id).then(() => {
					this.siteService.pageTitle = this.template.templateName;
				});
			}
		});
		this.getTemplates();
		this.siteService.headerButtons = [
			{ text: 'Save', class: 'success', callback: () => this.save() },
			{ text: 'Delete', class: 'danger', callback: () => this.delete() }
		];
		this.siteService.backButton = { title: 'Back to Templates', link: '/admin/campaigns/templates' };

		setTimeout(() => this.pageLoaded = true, 1000); //hack to prevent the once function from firing when the page is first loaded

	}
	getTemplates() {
		this.dataService.getTemplates().subscribe((data) => {
			this.templates = data;
		});
	}
	async refreshTemplate(id: string) {
		return new Promise<void>((resolve) => {
			this.dataService.getTemplate(id).subscribe((data) => {
				this.template = data ? new Template(data) : null;
				if (!this.template) {
					this.template = new Template({ templateID: id });
					this.template.templateName = 'New Template';
					this.isNew = true;
				}
				this.modeSelection = this.template.editorTypeID;
				resolve();
			});
		});

	}
	get isHtml(): boolean {
		return this.template?.editorTypeID === EditorType.Html;
	}
	editorLoaded(e: any) {
		if(this.template.templateJSON && this.template.templateJSON !== '{}') {
			setTimeout(() =>  this.emailEditor?.loadDesign(JSON.parse(this.template.templateJSON)), 1000);
		}
		setTimeout(() => {
			this.emailEditor?.editor.registerCallback('design:updated', () => {
				this.isDirty = true;
			});
		}, 1000);

	}

	/** Reads the live Unlayer editor (unsaved work included) and returns to Angular's zone. */
	private readDesigner(cb: (design: any, html: string) => void) {
		this.emailEditor.exportHtml((data: any) => this.zone.run(() => cb(data.design, data.html.replace(/&amp;/ig, '&'))));
	}

	/** Toggle handler. Switching is confirmed when it would discard or convert content. */
	requestModeChange(target: number) {
		if (target === this.template.editorTypeID) return;
		const revert = () => this.modeSelection = this.template.editorTypeID;

		if (target === EditorType.Html) {
			// Check the live editor: a design that hasn't been saved yet isn't in template.templateJSON.
			this.readDesigner((design, html) => {
				if (!unlayerDesignHasContent(design)) { this.switchToHtml('', null); return; }
				this.confirmationService.confirm({
					message: "Switch to Expert (HTML) mode? Your design will be converted to HTML as a starting point. If you switch back to the Designer, your design will be restored, but changes made in HTML won't carry over.",
					header: "Switch to Expert mode?",
					acceptLabel: "Switch",
					rejectLabel: "Cancel",
					acceptIcon: 'check',
					rejectIcon: 'times',
					accept: () => { this.switchToHtml(html, JSON.stringify(design)); this.importConvertedImages(); },
					reject: revert
				});
			});
		} else {
			const restoring = !!this.designBeforeHtml;
			if (!restoring && !this.template.templateHtml?.trim()) { this.switchToDesigner(); return; }
			this.confirmationService.confirm({
				message: restoring
					? "Switch back to the Designer? Your design from before you switched will be restored. Changes made in HTML will be discarded."
					: "Switch back to the Designer? The HTML in this template will be discarded.",
				header: "Discard HTML?",
				acceptLabel: "Discard and switch",
				acceptButtonStyleClass: 'danger',
				rejectLabel: "Cancel",
				acceptIcon: 'check',
				rejectIcon: 'times',
				accept: () => this.switchToDesigner(),
				reject: revert
			});
		}
	}
	/** After Designer → Expert, copy the design's images (hosted by Unlayer) to One Morning storage, as a paste would. */
	private importConvertedImages() {
		// The expert editor is created by *ngIf on the next change-detection pass.
		setTimeout(() => this.expertEditor?.reimportImages());
	}
	/** @param designBefore the design being converted, kept for switching back; undefined keeps the current copy. */
	private switchToHtml(html: string, designBefore?: string) {
		if (designBefore !== undefined) this.designBeforeHtml = designBefore;
		this.template.templateHtml = html;
		this.template.templateJSON = null;
		this.template.editorTypeID = EditorType.Html;
		this.modeSelection = EditorType.Html;
		this.isDirty = true;
	}
	/** @param designJson the design to open; defaults to the one kept from before switching to Expert. */
	private switchToDesigner(designJson: string = this.designBeforeHtml ?? this.template.templateJSON) {
		this.template.templateHtml = '';
		this.template.templateJSON = designJson;
		this.designBeforeHtml = null;
		this.template.editorTypeID = EditorType.Designer;
		this.modeSelection = EditorType.Designer;
		this.isDirty = true;
		// The <email-editor> is re-created by *ngIf and editorLoaded() loads templateJSON (if any).
	}

	selectTemplate() {
		if(!this.pageLoaded) { return; }
		const source = this.selectedTemplate;
		if (!source) return;
		const sourceIsHtml = source.editorTypeID === EditorType.Html;
		const sourceEmpty = sourceIsHtml ? !source.templateHtml?.trim() : (!source.templateJSON || source.templateJSON === '{}');
		if (sourceEmpty) return;

		const apply = () => {
			if (sourceIsHtml) {
				// From the Designer the current design is being replaced on purpose, so nothing is kept.
				this.switchToHtml(source.templateHtml, this.isHtml ? undefined : null);
			} else if (this.isHtml) {
				this.switchToDesigner(source.templateJSON);
			} else {
				this.emailEditor.loadDesign(JSON.parse(source.templateJSON));
				this.isDirty = true;
			}
		};

		const confirmReplace = () => this.confirmationService.confirm({
			message: "This template is not empty.  By selecting a new template, you will lose any changes you have made to this template.",
			header: "Are you sure?",
			acceptLabel: "Select Template",
			rejectLabel: "Cancel",
			acceptIcon: 'check',
			rejectIcon: 'times',
			accept: apply
		});

		if (this.isHtml) {
			if (this.template.templateHtml?.trim()) confirmReplace(); else apply();
		} else {
			// Check the live editor so unsaved design work counts as content.
			this.readDesigner(design => unlayerDesignHasContent(design) ? confirmReplace() : apply());
		}
	}
	canDeactivate(): boolean | Observable<boolean> {
		return !this.isDirty;
	}
	async save() {
		return new Promise<boolean>(async (resolve, reject) => {
			this.errors = {};
			if(!this.template.templateName) {
				this.errors.templateName = 'Template name is required';
				return;
			}
			if (this.isHtml) {
				if (this.expertEditor?.errorCount > 0) {
					this.errors.templateHtml = 'Fix the errors listed in the HTML issues panel before saving.';
					return;
				}
				this.template.templateJSON = null;
			} else {
				await new Promise<void>((resolve2) => {
					this.emailEditor.exportHtml((data: any) => {
						let html = data.html.replace(/&amp;/ig, '&');
						this.template.templateJSON = JSON.stringify(data.design);
						this.template.templateHtml = html; // validated server-side; not persisted for designer templates
						resolve2();
					});
				});
			}
			this.dataService.saveTemplate(this.template).subscribe(() => {
				this.siteService.setMessage('Template saved');
				this.isNew = false;
				this.refreshTemplate(this.template.templateID);
				this.isDirty = false;
				resolve(true);
			}, (err) => {
				this.errors = err.error?.errors ?? {};
				reject(false);
			});
		});
	}

	delete() {
		this.confirmationService.confirm({
			message: "Are you sure you want to delete this template?  This action cannot be undone.",
			header: "Delete Template?",
			acceptLabel: "Delete Template",
			acceptButtonStyleClass: 'danger',
			rejectLabel: "Cancel",
			acceptIcon: 'check',
			rejectIcon: 'times',
			accept: () => {
				this.dataService.deleteTemplate(this.template.templateID).subscribe(() => {
					this.siteService.setMessage('Template deleted');
					this.router.navigate(['/admin', 'campaigns', 'templates']);
				});
			}
		})
	}
}
